/**
 * Package Configurator — step 1 of the BV associate's execution-strategy flow.
 *
 * One row per catalog package (the complete 18-package execution universe).
 * Per included package the BV user decides:
 *   ✓ include · complexity (scope label) · days (judgement) · timing
 * Timing is one of Pre-possession / Post-possession / Deferred. For
 * Pre-possession the package carries TWO explicit builder-WBS fields —
 * "Start After [milestone]" and "Finish Before [milestone]" — either or both
 * (v3_32, user ask). The backend mode is derived: finish-before set →
 * FINISH_BEFORE, else START_AFTER.
 * Everything else (dates, dependencies, derived packages) is computed by the
 * engine. Impact-rule DERIVED rows appear automatically with a provenance chip.
 */
import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { Link2, Sparkles, AlertTriangle, Truck } from "lucide-react";
import { getPackageConfig, savePackageConfig } from "../../services/interiorExecution";
import { useWbsStages } from "../../constants/wbsStages";
import MoveInConfigModal from "./MoveInConfigModal";

const COMPLEXITIES = ["MINOR", "MODERATE", "MAJOR"];
const PRE_HANDOVER_MODES = ["PRE_HANDOVER_MUST_FINISH_BEFORE_WBS", "PRE_HANDOVER_START_AFTER_WBS"];

// User-facing timing — the two backend pre-handover modes collapse into one
// "Pre-possession" choice; the window fields below decide the exact semantics.
const TIMING_OPTIONS = [
  { value: "PRE", label: "Pre-possession" },
  { value: "POST_HANDOVER", label: "Post-possession" },
  { value: "DEFERRED", label: "Deferred" },
];
const timingOf = (mode) => (PRE_HANDOVER_MODES.includes(mode) ? "PRE" : mode);
// Backend mode from the window: a finish-before bound makes it deadline-driven.
const modeFromWindow = (it) =>
  it.finishBeforeWbsStageId ? "PRE_HANDOVER_MUST_FINISH_BEFORE_WBS" : "PRE_HANDOVER_START_AFTER_WBS";

const PackageConfigurator = ({ leadId, onReview }) => {
  const [items, setItems] = useState([]);
  const [warnings, setWarnings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [moveInOpen, setMoveInOpen] = useState(false);
  const { stages: wbsStages } = useWbsStages();

  const wbsOptions = useMemo(
    () => wbsStages.filter((s) => s.isActive && s.isConstruction),
    [wbsStages]
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await getPackageConfig(leadId);
        if (!cancelled) {
          setItems(res.items || []);
          setWarnings(res.warnings || []);
        }
      } catch (e) {
        if (!cancelled) toast.error(e?.response?.data?.detail || "Failed to load package configuration");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [leadId]);

  const update = (key, patch) => {
    setItems((prev) => prev.map((it) => (it.packageKey === key ? { ...it, ...patch, _touched: true } : it)));
  };

  // Complexity is a scope label: it drives the impact rules AND pre-fills the
  // days fields with the catalog suggestion — which the BV user then owns.
  const setComplexity = (it, c) => {
    update(it.packageKey, {
      complexity: c,
      durationDays: it.durationDaysByComplexity?.[c] ?? it.durationDays ?? "",
      manufacturingDays: it.hasSplitLifecycle
        ? (it.manufacturingDaysByComplexity?.[c] ?? it.manufacturingDays ?? "")
        : it.manufacturingDays,
    });
  };

  const handleSave = async () => {
    const included = items.filter((it) => it.isIncluded);
    if (included.length === 0) return toast.error("Include at least one package");
    setSaving(true);
    try {
      // Send every row the user touched + every included row (idempotent upsert).
      const windowless = included.find(
        (it) => PRE_HANDOVER_MODES.includes(it.executionMode) && !it.startAfterWbsStageId && !it.finishBeforeWbsStageId
      );
      if (windowless) {
        return toast.error(
          `${windowless.label || windowless.packageKey}: pick a "Start After" and/or "Finish Before" builder stage.`
        );
      }
      const payload = items
        .filter((it) => it._touched || it.isIncluded)
        .map((it) => ({
          packageKey: it.packageKey,
          isIncluded: it.isIncluded,
          // Pre-possession backend mode derives from the window (finish-before wins)
          executionMode: PRE_HANDOVER_MODES.includes(it.executionMode) ? modeFromWindow(it) : it.executionMode,
          // v3_31 window: a package can be bounded on both sides (start after X + finish before Y)
          startAfterWbsStageId: PRE_HANDOVER_MODES.includes(it.executionMode) ? it.startAfterWbsStageId : null,
          finishBeforeWbsStageId: PRE_HANDOVER_MODES.includes(it.executionMode) ? it.finishBeforeWbsStageId : null,
          complexity: it.complexity,
          durationDays: it.durationDays === "" || it.durationDays == null ? null : Number(it.durationDays),
          manufacturingDays:
            it.manufacturingDays === "" || it.manufacturingDays == null ? null : Number(it.manufacturingDays),
          notes: it.notes || null,
        }));
      const res = await savePackageConfig(leadId, payload);
      setItems(res.items || []);
      setWarnings(res.warnings || []);
      toast.success("Package configuration saved");
      if (onReview) onReview();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save configuration");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-2 p-1">
        {[...Array(6)].map((_, i) => <div key={i} className="h-12 rounded-2xl bg-slate-100 animate-pulse" />)}
      </div>
    );
  }

  return (
    <div>
      <p className="text-xs text-slate-500 mb-3">
        Tick what this unit's design includes; pick the scope and how each package runs against the builder's
        schedule. The engine derives every date — secondary packages implied by your choices are added
        automatically (marked <Sparkles size={11} className="inline text-violet-500" />).
      </p>

      {warnings.length > 0 && (
        <div className="mb-3 rounded-xl border border-amber-200 bg-amber-50 p-3 space-y-1">
          {warnings.map((w, i) => (
            <p key={i} className="text-xs text-amber-700 inline-flex items-start gap-1.5">
              <AlertTriangle size={12} className="mt-0.5 flex-shrink-0" /> {w.message || `${w.code} (${w.packageKey})`}
            </p>
          ))}
        </div>
      )}

      <div className="space-y-1.5">
        {items.map((it) => {
          const derived = it.source === "DERIVED";
          const preHandover = PRE_HANDOVER_MODES.includes(it.executionMode);
          return (
            <div
              key={it.packageKey}
              className={`rounded-xl border px-3 py-2.5 flex items-center gap-3 flex-wrap transition-colors ${
                it.isIncluded ? "border-flamingo-200 bg-flamingo-50/30" : "border-slate-200 bg-white"
              }`}
            >
              <label className="flex items-center gap-2.5 min-w-[220px] flex-1 cursor-pointer">
                <input
                  type="checkbox"
                  checked={it.isIncluded}
                  onChange={(e) => update(it.packageKey, { isIncluded: e.target.checked })}
                  className="h-4 w-4 rounded border-slate-300 text-flamingo-600 focus:ring-flamingo-500"
                />
                <span className={`text-sm font-medium ${it.isIncluded ? "text-slate-900" : "text-slate-500"}`}>
                  {it.label || it.packageKey}
                </span>
                {derived && (
                  <span
                    title={`Added automatically — required by ${it.derivedFromPackageKey || "an included package"}`}
                    className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[10px] font-semibold bg-violet-50 text-violet-600 border border-violet-200"
                  >
                    <Sparkles size={10} /> via {(it.derivedFromPackageKey || "rule").replace(/_/g, " ")}
                  </span>
                )}
              </label>

              {it.isIncluded && (
                <>
                  {/* complexity (scope label — pre-fills the days suggestion) */}
                  <div className="flex rounded-lg border border-slate-200 overflow-hidden">
                    {COMPLEXITIES.map((c) => (
                      <button
                        key={c}
                        onClick={() => setComplexity(it, c)}
                        className={`px-2 py-1 text-[10px] font-semibold uppercase ${
                          it.complexity === c ? "bg-slate-800 text-white" : "bg-white text-slate-500 hover:bg-slate-50"
                        }`}
                      >
                        {c.slice(0, 3)}
                      </button>
                    ))}
                  </div>

                  {/* BV-entered durations (pre-filled by complexity, freely editable) */}
                  <span className="inline-flex items-center gap-1" title="Execution duration — your judgement for this unit">
                    <input
                      type="number" min="1"
                      value={it.durationDays ?? it.durationDaysByComplexity?.[it.complexity] ?? ""}
                      onChange={(e) => update(it.packageKey, { durationDays: e.target.value })}
                      className="w-14 rounded-lg border border-slate-300 text-xs py-1.5 px-2 text-right focus:outline-none focus:ring-2 focus:ring-flamingo-500/40"
                    />
                    <span className="text-[10px] text-slate-400">days</span>
                  </span>
                  {it.hasSplitLifecycle && (
                    <span className="inline-flex items-center gap-1" title="Manufacturing / factory days (split-lifecycle package)">
                      <input
                        type="number" min="1"
                        value={it.manufacturingDays ?? it.manufacturingDaysByComplexity?.[it.complexity] ?? ""}
                        onChange={(e) => update(it.packageKey, { manufacturingDays: e.target.value })}
                        className="w-14 rounded-lg border border-slate-300 text-xs py-1.5 px-2 text-right focus:outline-none focus:ring-2 focus:ring-flamingo-500/40"
                      />
                      <span className="text-[10px] text-slate-400">mfg</span>
                    </span>
                  )}

                  {/* timing */}
                  <select
                    value={timingOf(it.executionMode)}
                    onChange={(e) => {
                      const v = e.target.value;
                      update(it.packageKey, {
                        executionMode: v === "PRE" ? modeFromWindow(it) : v,
                      });
                    }}
                    className="rounded-lg border border-slate-300 text-xs py-1.5 px-2 bg-white focus:outline-none focus:ring-2 focus:ring-flamingo-500/40"
                  >
                    {TIMING_OPTIONS.filter((o) =>
                      o.value === "PRE"
                        ? (it.allowedModes || []).some((m) => PRE_HANDOVER_MODES.includes(m))
                        : (it.allowedModes || []).includes(o.value)
                    ).map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>

                  {/* Builder WBS window — two explicit fields, each with the
                      full builder-stage dropdown (v3_32 user ask). Shown on its
                      own line so the labels read clearly. */}
                  {preHandover && (
                    <div className="w-full flex items-center gap-4 flex-wrap pl-7 pt-1">
                      <Link2 size={12} className="text-slate-400" />
                      <span className="inline-flex items-center gap-1.5">
                        <span className="text-[11px] font-medium text-slate-500 whitespace-nowrap">Start After</span>
                        <select
                          value={it.startAfterWbsStageId || ""}
                          onChange={(e) => update(it.packageKey, { startAfterWbsStageId: e.target.value ? Number(e.target.value) : null })}
                          className="rounded-lg border border-slate-300 text-xs py-1.5 px-2 bg-white min-w-[180px] focus:outline-none focus:ring-2 focus:ring-flamingo-500/40"
                          title="Work can start once this builder stage's Target End is reached"
                        >
                          <option value="">— none —</option>
                          {wbsOptions.map((s) => (
                            <option key={s.wbsStageId} value={s.wbsStageId}>{s.label || s.typeName}</option>
                          ))}
                        </select>
                      </span>
                      <span className="inline-flex items-center gap-1.5">
                        <span className="text-[11px] font-medium text-slate-500 whitespace-nowrap">Finish Before</span>
                        <select
                          value={it.finishBeforeWbsStageId || ""}
                          onChange={(e) => update(it.packageKey, { finishBeforeWbsStageId: e.target.value ? Number(e.target.value) : null })}
                          className="rounded-lg border border-slate-300 text-xs py-1.5 px-2 bg-white min-w-[180px] focus:outline-none focus:ring-2 focus:ring-flamingo-500/40"
                          title="Work must finish before this builder stage's Target Start"
                        >
                          <option value="">— none —</option>
                          {wbsOptions.map((s) => (
                            <option key={s.wbsStageId} value={s.wbsStageId}>{s.label || s.typeName}</option>
                          ))}
                        </select>
                      </span>
                    </div>
                  )}
                  {it.hasSplitLifecycle && it.executionMode === "POST_HANDOVER" && (
                    <span className="text-[10px] text-slate-400 italic">procure &amp; manufacture before possession</span>
                  )}

                  {/* Move-in Service: source/destination + move window (drives the move-in tender). */}
                  {it.packageKey === "MOVE_IN_SERVICE" && (
                    <button
                      type="button"
                      onClick={() => setMoveInOpen(true)}
                      className="inline-flex items-center gap-1.5 rounded-lg border border-flamingo-300 bg-white text-flamingo-700 text-xs font-semibold px-3 py-1.5 hover:bg-flamingo-50"
                    >
                      <Truck size={13} /> Configure move-in details
                    </button>
                  )}
                </>
              )}
            </div>
          );
        })}
      </div>

      <div className="flex justify-end mt-4">
        <button
          onClick={handleSave}
          disabled={saving}
          className="rounded-xl bg-flamingo-600 hover:bg-flamingo-700 disabled:opacity-60 text-white text-sm font-semibold px-5 py-2.5"
        >
          {saving ? "Saving…" : "Save & Review Strategy →"}
        </button>
      </div>

      <MoveInConfigModal leadId={leadId} open={moveInOpen} onClose={() => setMoveInOpen(false)} />
    </div>
  );
};

export default PackageConfigurator;
